import { useEffect, useRef, useState } from 'react';
import {
  DefaultValues,
  FieldValues,
  Path,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { getTassoQuintoErrorCode } from '../../../services/api/tasso-quinto';
import { AcquisitionOperation } from '../../../services/api/acquisition/acquisitionOperation';
import {
  AcquisitionOperationProduct,
  getAcquisitionOperation,
  retryAcquisitionOperation,
} from '../../../services/api/acquisition/acquisitionRecovery';
import {
  AcquisitionActivityStore,
  useAcquisitionActivityReport,
} from './acquisitionActivity';
import {
  reportAcquisitionFailure,
  reportIncompleteAcquisition,
} from './acquisitionDiagnostics';
import {
  acquisitionIncompleteNotice,
  acquisitionSuccessMessage,
  isAcquisitionRetryable,
} from './acquisitionOutcome';
import {
  forgetAcquisitionRecovery,
  readAcquisitionRecovery,
  rememberAcquisitionRecovery,
} from './acquisitionRecoveryStorage';
import { AcquisitionSessionStore } from './acquisitionSessions';

/**
 * How long the backend holds an operation under a worker's lease. A
 * `409 idempotency_in_progress` means someone else is inside that window, so
 * the operator is asked to wait it out rather than told the acquisition failed.
 */
export const ACQUISITION_LEASE_MS = 120_000;

/**
 * Whether a command about to be sent differs from the one the last send
 * carried. Both are built by the product's single `buildCommand`, so their keys
 * are written in one fixed order and serializing is a faithful comparison;
 * erring towards "edited" would only cost a key rotation, never a duplicate.
 */
const isEditOf = <TCommand,>(sent: TCommand | null, next: TCommand) =>
  sent !== null && JSON.stringify(sent) !== JSON.stringify(next);

export const ACQUISITION_RECOMPILE_MESSAGE =
  'Di questa acquisizione non è rimasto nulla di registrato e non può essere completata. Ricompila la richiesta: verrà inviata come una nuova acquisizione.';

interface AcquisitionPanelDefinition<TValues extends FieldValues, TCommand> {
  product: AcquisitionOperationProduct;
  /** Where this product's unfinished draft and command identity live. */
  sessions: AcquisitionSessionStore;
  /** What the workspace around the panel is told about this compilation. */
  activity: AcquisitionActivityStore;
  defaultValues: TValues;
  /** The answers the product's branching and qualification policy read. */
  watch: (keyof TValues & string)[];
  /** The conditional questions those answers do not ask. */
  inactiveFields: (answers: TValues) => (keyof TValues & string)[];
  /** Why the compiled profile cannot be acquired, in the operator's words. */
  ineligibility: (answers: TValues) => string | null;
  buildCommand: (values: TValues) => TCommand;
  createLead: (
    command: TCommand,
    idempotencyKey: string
  ) => Promise<AcquisitionOperation>;
  describeFailure: (error: unknown) => string;
}

/**
 * What an unresolved acquisition offers the operator. An incomplete operation
 * has exactly one way forward, so the state names that one rather than three
 * booleans a reader would have to prove mutually exclusive: the retry route
 * when durable work exists, the original command when the lead was never
 * written and the browser still holds it, and a fresh compilation otherwise.
 */
export type AcquisitionRecoveryMode = 'retry' | 'resubmit' | 'recompile';

export interface AcquisitionRecoveryState {
  operation: AcquisitionOperation | null;
  /** The single way forward, or `null` while there is nothing to resolve. */
  mode: AcquisitionRecoveryMode | null;
  /** Another worker holds the operation: retry waits, status may be refreshed. */
  leaseHeld: boolean;
  isBusy: boolean;
  message: string | null;
  retry: () => void;
  refreshStatus: () => void;
  resubmit: () => void;
  recompile: () => void;
}

/**
 * Everything an assisted acquisition does around its own questions. The
 * products differ in what they ask, what they compile and which policy judges
 * it; they do not differ in how a compilation is held, reviewed, sent once,
 * read back, recovered and ended, so that machinery is described here once and
 * each product supplies only its own answers to it.
 *
 * The product keeps the rendering: what is asked, in which words, under which
 * branch. This hook keeps the compilation's life — the draft that outlives the
 * tab, the command identity, the review gate, the single in-flight request, the
 * unresolved operation that survives a reload, and the outcome the operator
 * reads.
 */
export const useAcquisitionPanel = <TValues extends FieldValues, TCommand>({
  product,
  sessions,
  activity,
  defaultValues,
  watch,
  inactiveFields,
  ineligibility,
  buildCommand,
  createLead,
  describeFailure,
}: AcquisitionPanelDefinition<TValues, TCommand>) => {
  const [incompleteNotice, setIncompleteNotice] = useState<string | null>(null);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  /**
   * The command under review. Submitting the form compiles it once, and both
   * the review and the confirmed request read this same object: what the
   * operator approves is what leaves the browser.
   */
  const [reviewedCommand, setReviewedCommand] = useState<TCommand | null>(null);
  /** The incomplete operation the operator still has to resolve. */
  const [operation, setOperation] = useState<AcquisitionOperation | null>(null);
  /**
   * When the worker's lease on this operation can have lapsed. It is a deadline
   * rather than a flag because the lease is the backend's clock: a second
   * refusal restarts the window from the latest one, and nothing the operator
   * does — a status refresh included — can end it early.
   */
  const [leaseUntil, setLeaseUntil] = useState<number | null>(null);
  const [recoveryMessage, setRecoveryMessage] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  // A double click or a repeated key activation fires two confirmations before
  // `isPending` re-renders the disabled button; this ref closes that gap so
  // only one command per activation ever goes out.
  const isSubmittingRef = useRef(false);
  /**
   * The command the last send carried, kept only in memory. It is what makes an
   * unchanged resubmission possible after a failure and a lead-less incomplete
   * result recoverable — and what a reload deliberately does not keep, because
   * keeping it would mean persisting the customer.
   */
  const sentCommandRef = useRef<TCommand | null>(null);
  // As for a confirmation, a second activation can reach a resolution action
  // before the pending re-render disables it; one attempt per activation is
  // what keeps a retry from racing itself into the backend's lease.
  const isResolvingRef = useRef(false);

  const {
    control,
    getValues,
    handleSubmit,
    reset,
    unregister,
    formState: { errors, isDirty },
  } = useForm<TValues>({
    defaultValues: (sessions.getDraft<TValues>(product) ??
      defaultValues) as DefaultValues<TValues>,
  });

  // Leaving the tab unmounts the panel; an unfinished compilation is handed
  // back so the operator returns to it rather than to an empty form.
  const saveDraftRef = useRef<() => void>();
  saveDraftRef.current = () => {
    if (isDirty) sessions.setDraft(product, getValues());
  };
  useEffect(() => () => saveDraftRef.current?.(), []);

  const watched = useWatch({
    control,
    name: watch as unknown as Path<TValues>[],
  });

  // An unregistered field is absent from the watched values, so the answers
  // the branching and the policy read are completed back to the blank draft.
  const answers = { ...defaultValues } as TValues;
  watch.forEach((name, index) => {
    if (watched[index] !== undefined) {
      answers[name] = watched[index] as TValues[typeof name];
    }
  });

  // A conditional answer belongs to the branch that asked it. Once the branch
  // closes the field is unregistered rather than merely hidden: that drops its
  // value and its validation error together, so a stale answer can neither
  // reach the closed schema, nor block submission from a question nobody can
  // see, nor reappear if the operator opens the branch again. Each conditional
  // control carries `defaultValue=""`, so re-opening a branch re-registers a
  // controlled, blank input.
  const inactive = inactiveFields(answers);
  const inactiveSignature = inactive.join(',');
  useEffect(() => {
    if (inactive.length) {
      unregister(inactive as unknown as Path<TValues>[]);
    }
    // The field list is identified by its signature; rebuilding the array on
    // every render must not re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inactiveSignature, unregister]);

  /**
   * The public product's qualification policy, evaluated while the operator is
   * still compiling. It is a mirror of the backend's, never a second policy:
   * there is no override, and the same explanation answers a `422`.
   */
  const blockingMessage = ineligibility(answers);

  /** The compilation is over: nothing of it is held, stored or replayable. */
  const finishCompilation = (finished: AcquisitionOperation) => {
    setIncompleteNotice(null);
    setRecoveryMessage(null);
    setLeaseUntil(null);
    setOperation(null);
    sentCommandRef.current = null;
    toast.success(acquisitionSuccessMessage(finished.account));
    reset(defaultValues as DefaultValues<TValues>);
    forgetAcquisitionRecovery(product);
    // The next compilation is a new command, never a replay of the lead that
    // was just acquired, so the key rotates only now that it is complete.
    sessions.reset(product);
  };

  /** Durable work that is not finished: it is held, shown and made resolvable. */
  const holdIncomplete = (unfinished: AcquisitionOperation) => {
    setOperation(unfinished);
    setIncompleteNotice(acquisitionIncompleteNotice(unfinished));
    reportIncompleteAcquisition(product, unfinished);
    rememberAcquisitionRecovery(product, {
      operationId: unfinished.operationId,
      idempotencyKey: sessions.getIdempotencyKey(product),
    });
  };

  /** The operation the backend no longer knows: the hint is dropped, not shown. */
  const dropUnknownOperation = () => {
    forgetAcquisitionRecovery(product);
    setOperation(null);
    setIncompleteNotice(null);
    setLeaseUntil(null);
    // A `404` can also mean the key named an operation this actor may not see,
    // so that key may still be live on the backend. The next compilation must
    // not inherit it, or it would answer `409 idempotency_conflict` forever.
    sessions.reset(product);
  };

  const mutation = useMutation({
    mutationFn: (command: TCommand) =>
      createLead(command, sessions.getIdempotencyKey(product)),
    onSuccess: (created: AcquisitionOperation) => {
      setSubmissionError(null);

      // `outcome` is the only success signal: a `201` and a `200` replay of the
      // same completed operation both mean one acquired lead, and a `200`
      // carrying `incomplete` means durable work that is not finished.
      if (created.outcome !== 'success') {
        holdIncomplete(created);
        return;
      }

      finishCompilation(created);
    },
    onError: (error: unknown) => {
      // Nothing durable is known to exist, so the compilation stays editable
      // and keeps its key: an unchanged resubmission resumes the same command.
      setSubmissionError(describeFailure(error));
      reportAcquisitionFailure(product, error);
    },
    onSettled: () => {
      isSubmittingRef.current = false;
      // A success is announced by a toast and anything else on the page, above
      // the form the operator may need to correct: the review never stays in
      // the way of either.
      setReviewedCommand(null);
    },
  });

  /** A resolution attempt's answer: it either ends the operation or updates it. */
  const onResolved = (resolved: AcquisitionOperation) => {
    setRecoveryMessage(null);
    // The lease is deliberately left alone: reading an operation back says
    // nothing about whether the worker holding it has let go.
    if (resolved.outcome === 'success') {
      finishCompilation(resolved);
      return;
    }
    holdIncomplete(resolved);
  };

  /**
   * @param silent a restoration the operator never asked for. It may discover
   * that there is nothing to restore, and that is not news: the hint is dropped
   * and the page stays as if it had never been stored.
   */
  const onResolutionFailed = (error: unknown, silent = false) => {
    const code = getTassoQuintoErrorCode(error);

    // Another worker is inside the lease. That is a transient state of this
    // operation, not a conflict and not a failure: the form stays locked and
    // retry becomes available again once the lease can have lapsed.
    if (code === 'idempotency_in_progress') {
      setLeaseUntil(Date.now() + ACQUISITION_LEASE_MS);
      setRecoveryMessage(describeFailure(error));
      return;
    }

    // Unknown operation, wrong product, another actor, an unmatched key or an
    // operation past retention: there is nothing left to resolve, so the hint
    // goes rather than becoming an error the operator has to clear.
    if (code === 'acquisition_operation_not_found') {
      dropUnknownOperation();
      setRecoveryMessage(silent ? null : describeFailure(error));
      return;
    }

    setRecoveryMessage(silent ? null : describeFailure(error));
    reportAcquisitionFailure(product, error, operation);
  };

  const retryMutation = useMutation({
    mutationFn: (operationId: string) =>
      retryAcquisitionOperation(
        product,
        operationId,
        sessions.getIdempotencyKey(product)
      ),
    onSuccess: onResolved,
    onError: (error: unknown) => onResolutionFailed(error),
    onSettled: () => {
      isResolvingRef.current = false;
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ operationId }: { operationId: string; silent?: boolean }) =>
      getAcquisitionOperation(product, operationId),
    onSuccess: onResolved,
    onError: (error: unknown, variables) =>
      onResolutionFailed(error, variables.silent),
    onSettled: () => {
      isResolvingRef.current = false;
    },
  });

  /**
   * Reload recovery. Only the opaque triple survived, so the operation is read
   * back from the backend rather than reconstructed: the browser learns what
   * happened without ever having kept what was sent.
   */
  const restoreRef = useRef<() => void>();
  restoreRef.current = () => {
    const stored = readAcquisitionRecovery(product);
    if (!stored || operation) return;

    // The retry route requires the key that named the original command, so the
    // restored identity replaces the one this fresh page generated.
    sessions.adoptIdempotencyKey(product, stored.idempotencyKey);
    isResolvingRef.current = true;
    statusMutation.mutate({ operationId: stored.operationId, silent: true });
  };
  useEffect(() => {
    restoreRef.current?.();
    // The restoration runs once per mounted panel, on what storage holds then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The lease is a backend timer the browser cannot observe, only outlive. A
  // later deadline re-arms this, so a repeated refusal is waited out in full.
  useEffect(() => {
    if (leaseUntil === null) return undefined;
    const timer = setTimeout(
      () => setLeaseUntil(null),
      Math.max(0, leaseUntil - Date.now())
    );
    return () => clearTimeout(timer);
  }, [leaseUntil]);

  /** Submitting only opens the review; it never reaches the API. */
  const onSubmit = handleSubmit(
    (data) => {
      if (mutation.isPending || blockingMessage || operation) return;
      setReviewedCommand(buildCommand(data as TValues));
    },
    () => {
      // React Hook Form focuses the first invalid registered input; the selects
      // and the location autocomplete are not always focusable, so the first
      // field flagged invalid is brought into view as well.
      window.requestAnimationFrame(() => {
        formRef.current
          ?.querySelector<HTMLElement>('[aria-invalid="true"]')
          ?.scrollIntoView?.({ block: 'center' });
      });
    }
  );

  const send = (command: TCommand) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIncompleteNotice(null);
    setSubmissionError(null);
    setRecoveryMessage(null);
    sentCommandRef.current = command;
    mutation.mutate(command);
  };

  const confirmAcquisition = () => {
    if (!reviewedCommand) return;

    // The previous send left no durable record, and this command may not be the
    // one it carried. The same key with a different payload is a permanent
    // `409 idempotency_conflict`, so an edit becomes a new command first.
    if (isEditOf(sentCommandRef.current, reviewedCommand)) {
      sessions.rotateIdempotencyKey(product);
    }

    send(reviewedCommand);
  };

  const modeFor = (
    unresolved: AcquisitionOperation
  ): AcquisitionRecoveryMode => {
    if (isAcquisitionRetryable(unresolved)) return 'retry';
    return sentCommandRef.current ? 'resubmit' : 'recompile';
  };

  const isResolving = retryMutation.isPending || statusMutation.isPending;
  const leaseHeld = leaseUntil !== null;
  const isBusy = mutation.isPending || isResolving;

  // Whatever the panel is doing and whatever it would cost to leave, told to
  // the workspace: the tabs above it lock while a request is out, and an
  // unfinished compilation is worth a question before the route changes.
  useAcquisitionActivityReport(activity, product, {
    busy: isBusy,
    unsaved: isDirty || operation !== null,
  });

  const recovery: AcquisitionRecoveryState = {
    operation,
    mode: operation ? modeFor(operation) : null,
    leaseHeld,
    isBusy: isResolving || mutation.isPending,
    message: recoveryMessage,
    retry: () => {
      if (!operation || isResolvingRef.current || leaseHeld) return;
      isResolvingRef.current = true;
      setRecoveryMessage(null);
      retryMutation.mutate(operation.operationId);
    },
    refreshStatus: () => {
      if (!operation || isResolvingRef.current) return;
      isResolvingRef.current = true;
      setRecoveryMessage(null);
      statusMutation.mutate({ operationId: operation.operationId });
    },
    resubmit: () => {
      const command = sentCommandRef.current;
      if (!command) return;
      send(command);
    },
    recompile: () => {
      // Nothing durable exists under the old identity, so the fresh
      // compilation starts from a blank form and a key of its own.
      dropUnknownOperation();
      setRecoveryMessage(null);
      setSubmissionError(null);
      sentCommandRef.current = null;
      reset(defaultValues as DefaultValues<TValues>);
    },
  };

  return {
    control,
    errors,
    formRef,
    onSubmit,
    /** The current answers to the questions the product watches. */
    answers,
    blockingMessage,
    reviewedCommand,
    isPending: mutation.isPending,
    /** Any request is out: create, status or retry. Nothing may be started. */
    isBusy,
    /** An unresolved operation owns the compilation: the form must not change. */
    isLocked: !!operation,
    incompleteNotice,
    submissionError,
    recovery,
    confirmAcquisition,
    cancelReview: () => setReviewedCommand(null),
  };
};
